import {
  EMPTY_TAIWAN_TBCL_SET,
  EMPTY_TAIWAN_USAGE_MAP,
  getTaiwanTbclSet,
  getTaiwanUsageMap,
} from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import { useCallback } from "react";
import { getComponentPrevalenceLookups } from "@-/dev.improve.haniverse.data.component-prevalence";
import {
  getGraphemePrevalence,
  type GraphemePrevalenceResult,
} from "@-/dev.improve.haniverse.lib.morpheme.grapheme.prevalence";
import { useActorUser } from "@-/dev.improve.haniverse.lib.user";

/**
 * Returns a function that resolves prevalence for a grapheme using the user's `hanziScript`.
 *
 * The simplified + traditional component-prevalence closures (a whole-decomposition-graph BFS)
 * and the per-glyph Taiwan MOE usage map (plus its TBCL companion set) are all fully determined by
 * the static dataset, so they are precomputed at static-DB build time and read synchronously
 * ({@link getComponentPrevalenceLookups}, {@link getTaiwanUsageMap}, {@link getTaiwanTbclSet}) — no
 * on-device decomposition-map load, no per-startup BFS, and no whole-table grapheme scan for the
 * usage map (which previously cost ~2s prefetched at startup).
 */
export function useGetGraphemePrevalence(): (grapheme: string) => GraphemePrevalenceResult {
  const { hanziScript } = useActorUser();
  // Only the traditional ladder reads the Taiwan MOE data; Metro inlines the JSON requires, so a
  // simplified learner never loads the ~700 KB usage map into the heap.
  const isTraditional = hanziScript === "traditional";
  const twUsageMap = isTraditional ? getTaiwanUsageMap() : EMPTY_TAIWAN_USAGE_MAP;
  const twTbclSet = isTraditional ? getTaiwanTbclSet() : EMPTY_TAIWAN_TBCL_SET;
  const { simplified, traditional } = getComponentPrevalenceLookups();

  return useCallback(
    (grapheme: string) =>
      getGraphemePrevalence(grapheme, hanziScript, {
        simplifiedComponentPrevalenceLookup: simplified,
        twComponentPrevalenceLookup: traditional,
        twTbclSet,
        twUsageMap,
      }),
    [hanziScript, twTbclSet, twUsageMap, simplified, traditional],
  );
}
